import { useState } from "react";
import { motion } from "framer-motion";
import { Feather } from "lucide-react";
import { toast } from "sonner";
import { http, login } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const MOODS = ["Dream", "Secret", "Wish", "Memory"];

export const ThoughtForm = ({ onPosted }) => {
  const { user, loading } = useAuth();
  const [text, setText] = useState("");
  const [mood, setMood] = useState("Dream");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (!user)
    return (
      <div className="rounded-[22px] border border-purple-300/25 bg-[#0e0616]/60 backdrop-blur-xl p-8 text-center" data-testid="thought-login-prompt">
        <p className="font-display italic text-2xl md:text-3xl">Have something to leave in the hollow?</p>
        <button data-testid="thought-login-btn" onClick={login}
          className="mt-6 rounded-full bg-purple-200 text-[#0e0616] px-7 py-3 font-medium hover:bg-amber-200 transition-colors">
          Sign in with Google to write
        </button>
      </div>
    );

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await http.post("/thoughts", { text, mood });
      onPosted(data);
      setText("");
      toast("Your thought drifts into the hollow ✦");
    } catch {
      toast.error("Couldn't post right now. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.form onSubmit={submit} layout className="rounded-[22px] border border-purple-300/25 bg-[#0e0616]/65 backdrop-blur-xl p-6 md:p-8" data-testid="thought-form">
      <div className="flex items-center gap-3 text-purple-200 text-sm">
        <Feather className="w-4 h-4 text-amber-200" /> Writing as <span className="text-white">{user.name}</span>
      </div>
      <textarea data-testid="thought-input-textarea" value={text} onChange={(e) => setText(e.target.value)} maxLength={1200} rows={4}
        placeholder="Tonight I want to tell someone…" data-lenis-prevent
        className="mt-4 w-full bg-transparent font-display text-2xl leading-snug placeholder:text-purple-300/60 outline-none resize-none" />
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {MOODS.map((m) => (
            <button type="button" key={m} data-testid={`thought-mood-${m.toLowerCase()}`} onClick={() => setMood(m)}
              className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${mood === m ? "bg-purple-200 text-[#0e0616] border-purple-200" : "border-purple-300/30 hover:border-purple-200"}`}>
              {m}
            </button>
          ))}
        </div>
        <button data-testid="thought-submit-btn" disabled={busy || text.trim().length < 3}
          className="rounded-full bg-amber-200 text-[#0e0616] px-7 py-3 font-medium hover:bg-purple-200 transition-colors disabled:opacity-50">
          {busy ? "Sending…" : "Release thought"}
        </button>
      </div>
    </motion.form>
  );
};
